// "My edits": every change the writer has made in the editor, against the text as it was sent to them, each with a place to jump to.
// Paragraphs (stanzas, for poems) are the blocks between empty lines; they are matched up word for word, so a changed one shows
// exactly which words changed, and an added or removed one shows as that.
import type { ProofNoteText } from "./types";

export type EditKind = "title" | "words" | "added" | "removed" | "split" | "joined" | "note";

export interface Edit {
  id: string;
  pieceId: string;
  pieceTitle: string;
  kind: EditKind;
  /** The paragraph (block index, now) or footnote (its number) to jump to; null = the top of the piece. */
  target: { block: number } | { note: number } | null;
  text: string;
  /** Word-by-word comparison, as HTML (<del>/<ins>, escaped). */
  diff?: string;
}

export interface PieceState {
  pieceId: string;
  title: string;
  text: string;
  notes?: ProofNoteText[];
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
/** The words of a block as the reader sees them: no formatting marks, footnote marks shown as ¹ ² ³… */
const SUP = "⁰¹²³⁴⁵⁶⁷⁸⁹";
export const plain = (t: string) =>
  t.replace(/<\/?[ib]>/gi, "").replace(/\[\^(\d{1,3})\]/g, (_m, n: string) => [...n].map((d) => SUP[Number(d)]).join("")).replace(/\s+/g, " ").trim();
const marks = (t: string) => (t.match(/<\/?[ib]>/gi) ?? []).join("").toLowerCase();

/** A piece's paragraphs (or stanzas): the blocks between empty lines. */
export const blocksOf = (text: string) => text.replace(/\r/g, "").trim().split(/\n[ \t]*\n+/).filter((b) => b.trim());

/** Longest common subsequence alignment of two lists: pairs of [i in a | -1, j in b | -1] in order. */
function align<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): Array<[number, number]> {
  const n = a.length, m = b.length;
  const L: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i]![j] = eq(a[i]!, b[j]!) ? L[i + 1]![j + 1]! + 1 : Math.max(L[i + 1]![j]!, L[i]![j + 1]!);
  const out: Array<[number, number]> = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (eq(a[i]!, b[j]!)) { out.push([i++, j++]); }
    else if (L[i + 1]![j]! >= L[i]![j + 1]!) out.push([i++, -1]);
    else out.push([-1, j++]);
  }
  while (i < n) out.push([i++, -1]);
  while (j < m) out.push([-1, j++]);
  return out;
}

/** The words that changed, with a few unchanged words around each change. */
export function wordDiff(before: string, after: string, context = 6): string {
  const a = before.split(/\s+/).filter(Boolean), b = after.split(/\s+/).filter(Boolean);
  if (a.length * b.length > 4_000_000) return `<del>${esc(before)}</del> <ins>${esc(after)}</ins>`;
  const ops = align(a, b, (x, y) => x === y).map(([i, j]) => (i >= 0 && j >= 0 ? { t: "=", w: a[i]! } : i >= 0 ? { t: "-", w: a[i]! } : { t: "+", w: b[j]! }));
  const keep = ops.map((o, k) => o.t !== "=" || ops.slice(Math.max(0, k - context), k + context + 1).some((x) => x.t !== "="));
  let out = "", gap = false;
  ops.forEach((o, k) => {
    if (!keep[k]) { if (!gap) out += "… "; gap = true; return; }
    gap = false;
    out += o.t === "=" ? `${esc(o.w)} ` : o.t === "-" ? `<del>${esc(o.w)}</del> ` : `<ins>${esc(o.w)}</ins> `;
  });
  return out.replace(/<\/del> <del>/g, " ").replace(/<\/ins> <ins>/g, " ").trim();
}

/** Every change, piece by piece, in reading order. */
export function listEdits(original: PieceState[], now: Record<string, PieceState>, unit: "paragraph" | "stanza"): Edit[] {
  const out: Edit[] = [];
  for (const o of original) {
    const c = now[o.pieceId];
    if (!c) continue;
    const t = c.title || o.title || "(untitled)";
    const add = (e: Omit<Edit, "id" | "pieceId" | "pieceTitle">) => out.push({ ...e, id: `${o.pieceId}:${out.length}`, pieceId: o.pieceId, pieceTitle: t });
    if (o.title.trim() !== c.title.trim()) add({ kind: "title", target: null, text: "Title", diff: wordDiff(o.title, c.title) });
    const A = blocksOf(o.text), B = blocksOf(c.text);
    // first match identical blocks, then pair what's left between them as changed blocks
    const pairs = align(A, B, (x, y) => plain(x) === plain(y) && marks(x) === marks(y));
    let k = 0;
    while (k < pairs.length) {
      const [i, j] = pairs[k]!;
      if (i >= 0 && j >= 0) { k++; continue; }
      const gone: number[] = [], came: number[] = [];
      while (k < pairs.length && (pairs[k]![0] < 0 || pairs[k]![1] < 0)) {
        if (pairs[k]![0] >= 0) gone.push(pairs[k]![0]); else came.push(pairs[k]![1]);
        k++;
      }
      const flat = (xs: number[], from: string[]) => xs.map((x) => plain(from[x]!)).join(" ");
      const U = unit === "stanza" ? "stanza" : "paragraph";
      // one block broken in two (or more), or blocks pulled together, with the words unchanged
      if (gone.length === 1 && came.length > 1 && flat(gone, A) === flat(came, B)) {
        add({ kind: "split", target: { block: came[1]! }, text: `Split ${U} ${came[0]! + 1} into ${came.length}, before “${plain(B[came[1]!]!).slice(0, 50)}…”` });
        continue;
      }
      if (came.length === 1 && gone.length > 1 && flat(gone, A) === flat(came, B)) {
        add({ kind: "joined", target: { block: came[0]! }, text: `Joined ${gone.length} ${U}s into ${U} ${came[0]! + 1}` });
        continue;
      }
      const both = Math.min(gone.length, came.length);
      for (let x = 0; x < both; x++) {
        const was = A[gone[x]!]!, is = B[came[x]!]!;
        const label = `${unit === "stanza" ? "Stanza" : "Paragraph"} ${came[x]! + 1}`;
        if (plain(was) !== plain(is)) add({ kind: "words", target: { block: came[x]! }, text: label, diff: wordDiff(plain(was), plain(is)) });
        else add({ kind: "words", target: { block: came[x]! }, text: `${label}: italics or bold changed` });
      }
      for (const j2 of came.slice(both)) add({ kind: "added", target: { block: j2 }, text: `New ${unit}: “${plain(B[j2]!).slice(0, 70)}${plain(B[j2]!).length > 70 ? "…" : ""}”` });
      for (const i2 of gone.slice(both)) add({ kind: "removed", target: null, text: `Took out the ${unit} “${plain(A[i2]!).slice(0, 70)}${plain(A[i2]!).length > 70 ? "…" : ""}”` });
    }
    const cn = new Map((c.notes ?? []).map((x) => [x.n, x.text]));
    for (const x of o.notes ?? []) {
      const now2 = cn.get(x.n);
      if (now2 !== undefined && plain(now2) !== plain(x.text)) add({ kind: "note", target: { note: x.n }, text: `Footnote ${x.n}`, diff: wordDiff(plain(x.text), plain(now2)) });
    }
  }
  return out;
}
